import { useEffect, useRef } from 'react'

/**
 * Scrolls the page back to the top whenever `key` changes, so switching to a
 * different section starts reading from its beginning instead of dropping the
 * user mid-page.
 *
 * Runs in an effect rather than in the change handler so the scroll fires
 * *after* React commits the new content. Doing it in the handler races the
 * layout: switching from a long section to a shorter one let the page height
 * shrink out from under the smooth scroll and cancelled it, so only one
 * direction visibly worked.
 *
 * Every plausible scroll root is targeted (`window`, `scrollingElement`,
 * `documentElement`, `body`) because this app's CSS makes `<body>` the real
 * scroll container (see `index.scss`) while `document.scrollingElement` still
 * returns `<html>` in standards mode — using only one misses the actual
 * scroller in some browsers.
 *
 * The scroll is re-applied on the next animation frame because sections that
 * mount a `GRTable` drive `useWindowVirtualizer`, which measures window scroll
 * on commit and can re-publish a stale offset immediately after the first
 * `scrollTo(0)`; browser scroll-anchoring on content swap can do the same. The
 * second pass wins once layout settles.
 *
 * The first render is skipped so deep links and refreshes don't fight the
 * user's restored scroll position.
 *
 * @param key Changes to this value trigger the reset.
 * @param shouldSkip Consulted at reset time; return `true` to leave the scroll
 * position alone for a change that scrolls itself (e.g. a cross-tab deep link
 * that jumps to a specific card).
 */
export const useScrollToTopOnChange = (key: unknown, shouldSkip?: () => boolean): void => {
  // Held in a ref so a changing predicate identity can't re-trigger the reset.
  const shouldSkipRef = useRef(shouldSkip)
  shouldSkipRef.current = shouldSkip

  const isInitialRenderRef = useRef(true)
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    if (shouldSkipRef.current?.()) return

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'auto' })
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }
    scrollToTop()
    const raf = requestAnimationFrame(scrollToTop)
    return () => cancelAnimationFrame(raf)
  }, [key])
}
